import { decodeSeaNetwork, type PortRecord, parsePortsTsv, type SeaNetwork } from '@felix/domain';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { catalogOf, type PortCatalog } from './legs.js';
import type { CompanyPort } from './types.js';

/**
 * Paketli liman listesi ve deniz ağı (ADR 0048 §1–§2): `@felix/sea-data` ayrı (tembel yüklenen)
 * parçadır; gemide internetsiz çalışır. Bir kez yüklenir ve çözülür.
 */
export interface SeaData {
  packaged: PortRecord[];
  network: SeaNetwork;
  networkSource: string;
  attribution: { ports: string; network: string };
}

let seaData: Promise<SeaData> | null = null;

export function loadSeaData(): Promise<SeaData> {
  seaData ??= import('@felix/sea-data').then((data) => ({
    packaged: parsePortsTsv(data.PORTS_TSV),
    network: decodeSeaNetwork(data.MARNET),
    networkSource: data.MARNET.source,
    attribution: data.SEA_DATA_ATTRIBUTION,
  }));
  seaData.catch(() => {
    seaData = null;
  });
  return seaData;
}

interface PortDataValue {
  /** Şirketin limanları (ofisten iner); verilmezse yalnız paketli liste. */
  companyPorts?: () => Promise<CompanyPort[]>;
}

const PortDataContext = createContext<PortDataValue>({});

/** Uygulama kabuğu şirket limanlarının kaynağını verir (web: ofis API, gemi: IPC). */
export function PortDataProvider({
  companyPorts,
  children,
}: {
  companyPorts?: () => Promise<CompanyPort[]>;
  children: ReactNode;
}) {
  const value = useMemo(() => (companyPorts ? { companyPorts } : {}), [companyPorts]);
  return <PortDataContext.Provider value={value}>{children}</PortDataContext.Provider>;
}

export interface PortCatalogState {
  catalog: PortCatalog | null;
  sea: SeaData | null;
  companyPorts: CompanyPort[];
  failed: boolean;
  refresh: () => void;
}

/** Liman listesi + şirket limanları ve deniz ağı. Şirket limanı okunamazsa paketli liste kalır. */
export function usePortCatalog(): PortCatalogState {
  const { companyPorts } = useContext(PortDataContext);
  const [sea, setSea] = useState<SeaData | null>(null);
  const [company, setCompany] = useState<CompanyPort[]>([]);
  const [failed, setFailed] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    loadSeaData()
      .then((data) => live && setSea(data))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!companyPorts) return;
    let live = true;
    void round;
    companyPorts()
      .then((list) => live && setCompany(list))
      .catch(() => live && setCompany([]));
    return () => {
      live = false;
    };
  }, [companyPorts, round]);

  const catalog = useMemo(
    () => (sea ? catalogOf(sea.packaged, sea.network, company) : null),
    [sea, company],
  );
  const refresh = useCallback(() => setRound((n) => n + 1), []);
  return { catalog, sea, companyPorts: company, failed, refresh };
}

/** Ülke kodundan arayüz dilinde ülke adı (ADR 0048 §1: ad saklanmaz). */
export function useCountryName(): (code: string) => string {
  const { i18n } = useTranslation();
  return useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' });
    } catch {
      names = null;
    }
    return (code: string) => {
      try {
        return names?.of(code) ?? code;
      } catch {
        return code;
      }
    };
  }, [i18n.language]);
}
